import '@/assets/styles/main.scss';
import { getHostname, glsClearLocalStorageWhenAppVersionChange } from '@gls-its-frontends/common/gls-app-config';
import { glsInjectDynatrace } from '@gls-its-frontends/common/gls-dynatrace';
import { glsVueQueryPluginOptions } from '@gls-its-frontends/common/gls-query';
import { glsCreateI18nInstance } from '@gls-its-frontends/common/gls-translate';
import { VueQueryPlugin } from '@tanstack/vue-query';
import { createPinia, Pinia } from 'pinia';
import { createPersistedState } from 'pinia-plugin-persistedstate';
import { createApp, type Plugin } from 'vue';
import en from '@/assets/i18n/en.json';
import { LOCAL_STORAGE_PREFIX } from '@/constants/local-storage-prefix';
import { Environment } from '@/enums/environment';
import EnvironmentHostName from '@/enums/environment-hostname';
import { getEnvironment } from '@/helpers/get-environment';
import { setUpEnvironmentConfig } from '@/helpers/set-up-environment-config';
import { FeatureFlagService } from '@/services/feature-flag-service';
import { VersionService } from '@/services/version-service';

const STAGE_DYNATRACE_RUM_APP_ID: string = '50854838101c12f6';
const MT1_DYNATRACE_RUM_APP_ID: string = 'd991b0648678a468';
const PROD_DYNATRACE_RUM_APP_ID: string = '7b2edeca1c99bf14';

const setUpDynatrace = (): void => {
    switch (getHostname()) {
        case EnvironmentHostName.STAGE:
            glsInjectDynatrace(STAGE_DYNATRACE_RUM_APP_ID);
            break;
        case EnvironmentHostName.MT1:
            glsInjectDynatrace(MT1_DYNATRACE_RUM_APP_ID);
            break;
        case EnvironmentHostName.PROD:
            glsInjectDynatrace(PROD_DYNATRACE_RUM_APP_ID);
            break;
    }
};

(async (): Promise<void> => {
    const environment: Environment = getEnvironment();

    await FeatureFlagService.init();
    await VersionService.init();
    glsClearLocalStorageWhenAppVersionChange(APP_VERSION, LOCAL_STORAGE_PREFIX);
    setUpEnvironmentConfig(environment);

    setUpDynatrace();

    const { default: App } = await import('@/App.vue');
    const { default: router } = await import('@/routing/router');

    const app = createApp(App);

    const pinia: Pinia = createPinia().use(
        createPersistedState({
            key: (key: string): string => LOCAL_STORAGE_PREFIX + key
        })
    );
    const i18n: Plugin = glsCreateI18nInstance({ en });

    app.use(pinia).use(i18n).use(VueQueryPlugin, glsVueQueryPluginOptions).use(router).mount('#app');
})();
